import { z } from 'zod';
import {
  isWorkbookErrorCell,
  type ReportFilter,
  type WorkbookCell,
  type WorkbookDefinition,
  type WorkbookDiagnostic,
  type WorkbookRun,
  type WorkbookSamplingReport,
  type WorkbookValidation,
} from '@erp/shared-types';
import {
  describeRunModeBlocker,
  isRelationalValidation,
  runModeOption,
  suggestSamplingKeys,
  type WorkbookPlan,
} from '@erp/workbooks';
import { stableSerialize } from '@erp/shared-utils';
import { WorkbookAgentEditError } from './workbookAgentEdits';
import { agentRunSummaries } from './workbookAgentReads';

/**
 * A pinned example the agent checks on a Run on sample and throws away: the
 * rows of `worksheet` whose `match` columns read as given should read as
 * `expect` in the others. Never saved: examples go stale month to month.
 */
export const sampleAssertionSchema = z.object({
  worksheet: z.string().min(1).max(200).describe('The worksheet name.'),
  match: z
    .record(z.string(), z.string())
    .describe(
      'Columns that pick the example rows, each value as the column shows it (e.g. {"Item":"A100","Branch":"East"}).',
    ),
  expect: z
    .record(z.string(), z.string())
    .describe(
      'What those rows should show in other columns, as text (e.g. {"Recommended Floor":"6"}).',
    ),
});
export type SampleAssertion = z.infer<typeof sampleAssertionSchema>;

/** A cell as the agent compares it: its text, an Error cell's error value, Empty as "". */
function cellText(cell: WorkbookCell | undefined): string {
  if (cell === null || cell === undefined) return '';
  if (isWorkbookErrorCell(cell)) return cell.error;
  return String(cell);
}

/**
 * How a pinned example fared: its rows' failing columns, or why it couldn't
 * be checked. `matchedRowCount` is how many rows match in all; when `rows`
 * holds fewer, the unread ones could fail, so it is not checked rather than
 * passed.
 */
export function checkSampleAssertion(
  assertion: SampleAssertion,
  columns: readonly string[],
  rows: readonly WorkbookCell[][],
  matchedRowCount: number = rows.length,
) {
  const index = (name: string) =>
    columns.findIndex((column) => column.toLowerCase() === name.toLowerCase());
  // A match column the worksheet lacks would filter nothing, so every row would be checked.
  const missing = [
    ...new Set([
      ...Object.keys(assertion.match),
      ...Object.keys(assertion.expect),
    ]),
  ].filter((name) => index(name) < 0);
  if (missing.length)
    return {
      ...assertion,
      outcome: 'not-checked' as const,
      reason: `The worksheet has no column ${missing.map((name) => `'${name}'`).join(', ')}.`,
    };
  if (!rows.length)
    return {
      ...assertion,
      outcome: 'not-checked' as const,
      reason:
        'No row of the sample matches. Sample by a key that includes this example, or check it on a full run.',
    };
  if (matchedRowCount > rows.length)
    return {
      ...assertion,
      outcome: 'not-checked' as const,
      reason: `${matchedRowCount} rows match and only ${rows.length} were read. Add match columns until the example names fewer rows.`,
    };
  const mismatches = rows.flatMap((row) =>
    Object.entries(assertion.expect).flatMap(([name, expected]) => {
      const actual = cellText(row[index(name)]);
      return actual === expected ? [] : [{ column: name, expected, actual }];
    }),
  );
  return {
    ...assertion,
    outcome: mismatches.length ? ('failed' as const) : ('passed' as const),
    rowCount: rows.length,
    ...(mismatches.length && { mismatches: mismatches.slice(0, 10) }),
  };
}

/** A Representativeness report as the agent reads it: its figures, less the per-column detail. */
export function agentSampleReport(report: WorkbookSamplingReport) {
  return {
    keys: report.keys,
    distance: report.distance,
    warnings: report.warnings,
    sources: report.sources,
    strata: report.strata.slice(0, 30),
    lookups: report.lookups,
  };
}

const PROPOSE_RELATIONAL =
  'Relational checks (in-sheet validations) are saved only by the user: propose one with propose_worksheet_check instead.';

function relationalRules(definition: WorkbookDefinition): Map<string, string> {
  return new Map(
    definition.sheets.flatMap((sheet) =>
      (sheet.validations ?? [])
        .filter((validation: WorkbookValidation) =>
          isRelationalValidation(validation),
        )
        .map((validation): [string, string] => [
          validation.id,
          stableSerialize([sheet.id, validation]),
        ]),
    ),
  );
}

/**
 * Refuses an agent edit that adds or changes a relational check: those
 * relate worksheets the user vouches for, so the agent only proposes them.
 * Removing one (with its worksheet, say) is allowed.
 */
export function requireNoAgentRelationalRules(
  before: WorkbookDefinition,
  after: WorkbookDefinition,
): void {
  const existing = relationalRules(before);
  for (const [id, rule] of relationalRules(after))
    if (existing.get(id) !== rule)
      throw new WorkbookAgentEditError(PROPOSE_RELATIONAL);
}

/**
 * Why a Run on sample can't start now, for the agent: the state machine's
 * reason, with suggested keys when the Workbook has no Sampling yet. Null
 * when it can start.
 */
export function sampleRunRefusal(
  definition: WorkbookDefinition,
  diagnostics: readonly WorkbookDiagnostic[],
  plan: WorkbookPlan,
) {
  const { blocker } = runModeOption(
    { definition, diagnostics, runs: [] },
    'sample',
    null,
  );
  if (!blocker) return null;
  return {
    ok: false as const,
    error: describeRunModeBlocker(definition, blocker),
    ...(blocker.code === 'no-sampling' && {
      suggestedKeys: suggestSamplingKeys(definition, plan),
    }),
  };
}

/**
 * How a pinned example is read: the Worksheet it names and the Report
 * filters picking its rows, or why it can't be checked on `run`.
 */
export function sampleAssertionRead(
  assertion: SampleAssertion,
  definition: WorkbookDefinition,
  run: Pick<WorkbookRun, 'status'>,
):
  { sheetId: string; filters: ReportFilter[] } | ReturnType<typeof notChecked> {
  const lower = assertion.worksheet.toLowerCase();
  const sheet = definition.sheets.find(
    (item) => item.name.toLowerCase() === lower,
  );
  if (!sheet)
    return notChecked(
      assertion,
      `'${assertion.worksheet}' isn't a worksheet of this workbook.`,
    );
  if (run.status !== 'completed')
    return notChecked(assertion, 'The run failed.');
  return {
    sheetId: sheet.id,
    filters: Object.entries(assertion.match).map(([column, value], index) => ({
      id: `match-${index}`,
      column,
      kind: 'values' as const,
      values: [value],
    })),
  };
}

function notChecked(assertion: SampleAssertion, reason: string) {
  return { ...assertion, outcome: 'not-checked' as const, reason };
}

/** A finished Run on sample as run_workbook_sample returns it: bounded rows, its report, and the pinned examples' outcomes. */
export function agentSampleRun(
  run: WorkbookRun,
  definition: WorkbookDefinition,
  rowLimit: number,
  assertions: readonly unknown[],
) {
  const [summary] = agentRunSummaries([run], definition, 1);
  const names = new Map(
    definition.sheets.map((sheet) => [sheet.id, sheet.name]),
  );
  const sampling = run.sampling;
  return {
    ...summary,
    worksheets: run.results.map((result) => ({
      worksheet: names.get(result.sheetId) ?? 'a removed worksheet',
      columns: result.columns,
      rowCount: result.rowCount,
      rows: result.rows.slice(0, rowLimit),
    })),
    ...(sampling?.report && { report: agentSampleReport(sampling.report) }),
    ...(sampling?.reusedRunId && { reusedSampleFrom: sampling.reusedRunId }),
    ...(assertions.length && { assertions }),
  };
}
